import { FormikProps } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { getFeedFixtureById } from '@/api/feed/getFeedFixtureById';
import { queryKeys } from '@/api/react-query.config';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useGetLeaderCandidatesQuery, LeaderCandidate } from '@/api/questions/getLeaderCandidates';
import { useGetFeedFixturesQuery, type FeedFixture } from '@/api/feed';
import { useGetFeedTeamsQuery } from '@/api/feed/getFeedTeams';
import { useGetFeedStatTypesQuery } from '@/api/feed';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * LeaderQuestionSection (DKPLAT-354)
 *
 * Shown only when question_type is `player_leader` or `team_leader`. Lets the
 * admin pick the stat to grade on and choose which players/teams to include
 * as options. The fixture set is inferred from the pickem's other fixture-
 * bound questions — see leader-candidates.controller.ts.
 *
 * Form-state contract on save (consumed by questionTransformers):
 *   values.question_metadata.leaderboard = {
 *     fixtureIds:  string[],    // returned by the candidate API
 *     statType:    string,      // admin-entered (e.g. 'passing_yards')
 *     statLabel?:  string,      // admin-entered display name (optional)
 *     kind:        'player' | 'team',
 *   }
 *   values.options = candidates the admin checked, transformed to the
 *                    standard option shape (external IDs + display data).
 */

interface LeaderQuestionSectionProps {
  formik: FormikProps<QuestionFormValues>;
  pickemId: string | undefined;
  /** Sport league id (e.g. 'nba', 'nfl'). Used to fetch the right stat list. */
  leagueId?: string;
  isEditable: boolean;
}

export function LeaderQuestionSection({
  formik,
  pickemId,
  leagueId,
  isEditable,
}: LeaderQuestionSectionProps) {
  const { values, setFieldValue } = formik;
  const isLeader =
    values.question_type === 'player_leader' || values.question_type === 'team_leader';
  const kind: 'player' | 'team' = values.question_type === 'team_leader' ? 'team' : 'player';

  // Local UI state — separate from form because the admin may toggle these
  // multiple times while configuring before committing to options.
  const meta = (values.question_metadata as any)?.leaderboard ?? {};
  const [statType, setStatType] = useState<string>(meta.statType ?? '');
  const [statLabel, setStatLabel] = useState<string>(meta.statLabel ?? '');
  const [showAll, setShowAll] = useState(false);
  // Auto-trigger when reopening a saved leader question — without this the
  // admin sees an empty candidate list until they click Reload candidates
  // (and there's nothing in the candidate-list area showing the saved
  // selections either).
  const hasSavedConfig = Boolean(
    meta.statType && Array.isArray(meta.fixtureIds) && meta.fixtureIds.length > 0
  );
  const [loadTriggered, setLoadTriggered] = useState(hasSavedConfig);

  // Admin's explicit fixture selection. Seeded from any previously-saved
  // metadata so re-opening the question preserves the choice. Empty array =
  // "admin hasn't picked any yet"; backend treats that as a hard stop
  // (we won't auto-resolve the window any more).
  const initialFixtureIds: string[] = Array.isArray(meta.fixtureIds) ? meta.fixtureIds : [];
  const [selectedFixtureIds, setSelectedFixtureIds] = useState<string[]>(initialFixtureIds);

  // Date range that defines which fixtures appear in the multi-select. The
  // backend defaults to pickem.startUtc → +30 days, but we let the admin
  // override here so a stale or misaligned startUtc doesn't hide upcoming
  // games. Format: 'YYYY-MM-DD' for <input type="date"> compatibility.
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const oneWeekOutStr = useMemo(
    () => new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    []
  );
  const [windowFrom, setWindowFrom] = useState<string>(todayStr);
  const [windowTo, setWindowTo] = useState<string>(oneWeekOutStr);

  // Window-fixtures list for the multi-select UI. Uses the proven
  // /feed/fixtures admin endpoint (same one the existing FixtureSelectionSection
  // calls successfully for team/player questions) instead of a leader-specific
  // route — keeps the date-range parsing / ET-timezone handling consistent.
  const { data: rawFixtures = [], isLoading: fixturesLoading } = useGetFeedFixturesQuery(
    leagueId,
    undefined,
    windowFrom || undefined,
    windowTo || undefined
  );
  // When editing an existing question, the saved fixtureIds can fall
  // outside the default date window (e.g. contest scheduled 3 weeks out).
  // Fetch each saved fixture by id and merge into the displayed list so
  // the multi-select can render them as ticked rather than appear empty.
  // Same hook the existing feed-fixtures endpoint uses, so caching and
  // team-resolution stay consistent.
  const savedFixtureQueries = useQueries({
    queries: initialFixtureIds.map(id => ({
      queryKey: queryKeys.feed.fixtureById(id),
      queryFn: () => getFeedFixtureById(id),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const windowFixtures = useMemo<FeedFixture[]>(() => {
    const byId = new Map<string, FeedFixture>();
    for (const f of rawFixtures) byId.set(f.externalId, f);
    // Merge saved-fixture results so previously-ticked games always appear
    // even when they're outside the current date window. rawFixtures wins
    // on collision since it's the live filtered list.
    for (const q of savedFixtureQueries) {
      if (q.data && !byId.has(q.data.externalId)) byId.set(q.data.externalId, q.data);
    }
    return [...byId.values()].sort((a, b) => {
      const aT = a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0;
      const bT = b.scheduledDate ? new Date(b.scheduledDate).getTime() : 0;
      return aT - bT;
    });
  }, [rawFixtures, savedFixtureQueries]);

  // Team lookup so the fixture list shows readable abbreviations ("LAD @ SF")
  // instead of raw external IDs (which are GUIDs for MLB). Same pattern the
  // squares FixtureSelectorInline uses.
  const { data: teams = [] } = useGetFeedTeamsQuery(leagueId);
  const teamMap = useMemo(() => {
    const map = new Map<string, { name: string; abbreviation?: string }>();
    teams.forEach(team => {
      map.set(team.externalId, { name: team.name, abbreviation: team.abbreviation });
    });
    return map;
  }, [teams]);

  // Selected option ids tracked from formik so re-renders preserve ticks.
  // On a fresh selection the admin form stuffs the external id onto the
  // form-only fields option_player_external_id / option_team_external_id.
  // On reload from the API those fields don't survive (the GET endpoint
  // collapses them into grading_data.playerId / teamId), so we fall back
  // to grading_data here — otherwise checkboxes appear unchecked on every
  // edit reopen.
  const selectedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const opt of values.options || []) {
      const ext =
        (opt as any).option_player_external_id ||
        (opt as any).option_team_external_id ||
        (opt as any).grading_data?.playerId ||
        (opt as any).grading_data?.teamId;
      if (ext) ids.add(String(ext));
    }
    return ids;
  }, [values.options]);

  // The question form initialises new questions with 2 empty option rows
  // (the standard pattern for team / player / prop). For leader questions
  // those empties are noise — options come from the candidate modal below.
  // Strip them once on mount so the form state matches what the admin sees.
  // We only remove options that have NO text and NO external id, so any
  // already-saved leader candidates survive reloads of the editor.
  useEffect(() => {
    if (!isLeader) return;
    const opts = values.options || [];
    const filtered = opts.filter(
      o =>
        !!(o as any).option_player_external_id ||
        !!(o as any).option_team_external_id ||
        (o.option_text_en && o.option_text_en.trim().length > 0)
    );
    if (filtered.length !== opts.length) {
      setFieldValue('options', filtered);
    }
    // intentionally only runs when the type flips to leader; keeping the
    // dependency narrow stops the effect from clobbering option edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLeader]);

  // Mirror local UI state (statType, statLabel, kind, selectedFixtureIds)
  // into formik's question_metadata.leaderboard whenever any of them
  // changes. Previously this only happened on "Reload candidates" click,
  // which meant editing-and-saving without reloading dropped the stat
  // label and fixture ids — customer modal then fell back to "points"
  // and the grader had no fixture window to walk.
  useEffect(() => {
    if (!isLeader) return;
    const currentLeaderboard =
      ((values.question_metadata as any)?.leaderboard as Record<string, unknown> | undefined) ?? {};
    const nextLeaderboard = {
      ...currentLeaderboard,
      statType: statType.trim() || undefined,
      statLabel: statLabel.trim() || statType.trim() || undefined,
      kind,
      fixtureIds: selectedFixtureIds,
    };
    // Cheap equality check — avoid setFieldValue churn that would
    // otherwise re-render every keystroke.
    const same =
      currentLeaderboard.statType === nextLeaderboard.statType &&
      currentLeaderboard.statLabel === nextLeaderboard.statLabel &&
      currentLeaderboard.kind === nextLeaderboard.kind &&
      Array.isArray(currentLeaderboard.fixtureIds) &&
      (currentLeaderboard.fixtureIds as string[]).length === selectedFixtureIds.length &&
      (currentLeaderboard.fixtureIds as string[]).every((id, i) => id === selectedFixtureIds[i]);
    if (same) return;
    setFieldValue('question_metadata', {
      ...((values.question_metadata as Record<string, unknown>) || {}),
      leaderboard: nextLeaderboard,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLeader, statType, statLabel, kind, selectedFixtureIds]);

  const { data, isLoading, isError, refetch } = useGetLeaderCandidatesQuery(
    {
      pickemId: pickemId || '',
      kind,
      statType: statType || undefined,
      all: showAll,
      fixtureIds: selectedFixtureIds.length > 0 ? selectedFixtureIds : undefined,
    },
    Boolean(pickemId) && loadTriggered && isLeader && selectedFixtureIds.length > 0
  );

  if (!isLeader) return null;

  const candidates = data?.candidates ?? [];
  const total = data?.total ?? 0;
  const truncated = data?.truncated ?? false;

  const handleToggleCandidate = (cand: LeaderCandidate) => {
    const existing = values.options || [];
    const idAttr = kind === 'team' ? 'option_team_external_id' : 'option_player_external_id';
    // Match by either the form-only ext-id field (set when ticked in this
    // session) OR the grading_data id (the only field that survives an API
    // round-trip). Without the grading_data branch, re-ticking a saved
    // candidate after edit would create a duplicate option row.
    const gradingKey = kind === 'team' ? 'teamId' : 'playerId';
    const alreadyIdx = existing.findIndex(
      (o: any) =>
        String(o[idAttr] ?? '') === String(cand.externalId) ||
        String(o.grading_data?.[gradingKey] ?? '') === String(cand.externalId)
    );

    if (alreadyIdx >= 0) {
      const next = [...existing];
      next.splice(alreadyIdx, 1);
      setFieldValue('options', next);
    } else {
      const newOption: any = {
        option_text_en: cand.name,
        option_text_fr: cand.name,
        option_image_en: cand.imageUrl,
        option_image_fr: cand.imageUrl,
        display_order: existing.length,
        [idAttr]: cand.externalId,
        grading_data: {
          [kind === 'team' ? 'teamId' : 'playerId']: cand.externalId,
          teamShortcode: cand.teamShortcode,
          playerPosition: cand.position,
          fixtureLabel: cand.fixtureLabel,
          matchTime: cand.matchTime,
          // Season-to-date total at save time. Live grader writes `liveValue`
          // once games are running; the customer modal prefers `liveValue`
          // when present and falls back to this `seasonValue` pre-game so
          // the two concepts stay distinct.
          seasonValue: cand.statValue,
        },
      };
      setFieldValue('options', [...existing, newOption]);
    }
  };

  const handleSelectAllVisible = () => {
    const existing = values.options || [];
    const idAttr = kind === 'team' ? 'option_team_external_id' : 'option_player_external_id';
    const next = [...existing];
    let nextOrder = existing.length;
    for (const cand of candidates) {
      if (selectedIds.has(String(cand.externalId))) continue;
      next.push({
        option_text_en: cand.name,
        option_text_fr: cand.name,
        option_image_en: cand.imageUrl,
        option_image_fr: cand.imageUrl,
        display_order: nextOrder++,
        [idAttr]: cand.externalId,
        grading_data: {
          [kind === 'team' ? 'teamId' : 'playerId']: cand.externalId,
          teamShortcode: cand.teamShortcode,
          playerPosition: cand.position,
          fixtureLabel: cand.fixtureLabel,
          matchTime: cand.matchTime,
          // See handleToggleCandidate above — store the admin's pre-game
          // season-to-date total in seasonValue, leave liveValue to the
          // live grader.
          seasonValue: cand.statValue,
        },
      } as any);
    }
    setFieldValue('options', next);
  };

  const handleClearSelections = () => {
    setFieldValue('options', []);
  };

  const handleLoad = () => {
    if (!statType.trim()) return;
    if (selectedFixtureIds.length === 0) return; // multi-select gates the load
    setLoadTriggered(true);
    // Push stat + fixture metadata into form state immediately so the form
    // save includes it even before the candidate response lands. The
    // grader reads from this metadata blob at grade time.
    setFieldValue('question_metadata', {
      ...(values.question_metadata || {}),
      leaderboard: {
        ...(meta || {}),
        statType: statType.trim(),
        statLabel: statLabel.trim() || statType.trim(),
        kind,
        fixtureIds: selectedFixtureIds,
      },
    });
    refetch();
  };

  const toggleFixture = (fixtureId: string) => {
    setSelectedFixtureIds(prev =>
      prev.includes(fixtureId) ? prev.filter(id => id !== fixtureId) : [...prev, fixtureId]
    );
    // Reset any loaded candidate state so the admin re-clicks Load after
    // changing the fixture set — avoids stale candidate rows from a
    // previous fixture choice persisting in the UI.
    setLoadTriggered(false);
  };

  const selectAllFixtures = () => {
    setSelectedFixtureIds(windowFixtures.map(f => f.externalId));
    setLoadTriggered(false);
  };

  const clearFixtures = () => {
    setSelectedFixtureIds([]);
    setLoadTriggered(false);
  };

  return (
    <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
      <div>
        <h3 className="text-base font-semibold">
          Leader configuration ({kind === 'team' ? 'Team' : 'Player'})
        </h3>
        <p className="text-sm text-muted-foreground">
          Pick which fixtures define the contest window, choose the stat to grade on, then load
          candidates from those fixtures and tick the ones to include as options.
        </p>
      </div>

      {/* Step 1: Fixture multi-select. Drives which fixtures the leader
          question grades across — stored on the question's
          questionMetadata.leaderboard.fixtureIds for the grader to walk. */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Step 1 — Fixtures in the contest window</Label>
          {windowFixtures.length > 0 && (
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={selectAllFixtures}
                disabled={!isEditable}
              >
                Select all ({windowFixtures.length})
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={clearFixtures}
                disabled={!isEditable || selectedFixtureIds.length === 0}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
        {/* Date range — defaults to today → +30 days, admin can shift the
            window so a stale pickem.startUtc doesn't hide upcoming games. */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="leader-window-from" className="text-xs">
              From
            </Label>
            <Input
              id="leader-window-from"
              type="date"
              value={windowFrom}
              onChange={e => setWindowFrom(e.target.value)}
              disabled={!isEditable}
              className="w-44"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="leader-window-to" className="text-xs">
              To
            </Label>
            <Input
              id="leader-window-to"
              type="date"
              value={windowTo}
              onChange={e => setWindowTo(e.target.value)}
              disabled={!isEditable}
              className="w-44"
            />
          </div>
          <span className="text-xs text-muted-foreground pb-1">
            Defaults to today → +7 days. Shift if the contest covers a different window.
          </span>
        </div>

        {!pickemId && (
          <p className="text-sm text-muted-foreground">Choose a pickem to load fixtures.</p>
        )}
        {pickemId && fixturesLoading && (
          <p className="text-sm text-muted-foreground">Loading fixtures…</p>
        )}
        {pickemId && !fixturesLoading && windowFixtures.length === 0 && (
          <p className="text-sm text-amber-600">
            No fixtures found between {windowFrom} and {windowTo}. Widen the range above or confirm
            the pickem sport is correct.
          </p>
        )}
        {windowFixtures.length > 0 && (
          <div className="border rounded max-h-72 overflow-y-auto bg-background">
            {windowFixtures.map(f => {
              const checked = selectedFixtureIds.includes(f.externalId);
              // Label is "AWAY @ HOME". Prefer readable team abbreviations
              // (or full names) from teamMap; fall back to the raw external
              // id only while teams are loading so the list never shows
              // GUID-style IDs once team data lands.
              const homeTeam = teamMap.get(f.homeTeamId);
              const awayTeam = teamMap.get(f.awayTeamId);
              const homeName = homeTeam?.abbreviation || homeTeam?.name || f.homeTeamId;
              const awayName = awayTeam?.abbreviation || awayTeam?.name || f.awayTeamId;
              const label = `${awayName} @ ${homeName}`;
              return (
                <label
                  key={f.externalId}
                  className={`flex items-center gap-3 px-3 py-2 border-b last:border-b-0 cursor-pointer hover:bg-muted/40 ${
                    checked ? 'bg-muted/30' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleFixture(f.externalId)}
                    disabled={!isEditable}
                  />
                  <div className="flex-1 text-sm">
                    <span className="font-medium">{label}</span>
                    {f.scheduledDate && (
                      <span className="text-muted-foreground ml-2">
                        {new Date(f.scheduledDate).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">{f.status}</span>
                </label>
              );
            })}
          </div>
        )}
        {selectedFixtureIds.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {selectedFixtureIds.length} fixture{selectedFixtureIds.length === 1 ? '' : 's'}{' '}
            selected.
          </p>
        )}
      </div>

      <Label className="mt-2">Step 2 — Stat</Label>
      <LeaderStatTypePicker
        leagueId={leagueId}
        kind={kind}
        selectedStatType={statType}
        selectedStatLabel={statLabel}
        disabled={!isEditable}
        onSelect={(code, label) => {
          setStatType(code);
          setStatLabel(label);
        }}
      />

      <div className="flex flex-col gap-2">
        <Label htmlFor="leader-stat-label">Customer-facing label (optional)</Label>
        <Input
          id="leader-stat-label"
          value={statLabel}
          onChange={e => setStatLabel(e.target.value)}
          placeholder="Defaults to the stat name above"
          disabled={!isEditable}
        />
        <span className="text-xs text-muted-foreground">
          Shown to customers in the modal title. Leave blank to use the stat's display name.
        </span>
      </div>

      <Label className="mt-2">Step 3 — Candidates</Label>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="default"
          onClick={handleLoad}
          disabled={!isEditable || !statType.trim() || !pickemId || selectedFixtureIds.length === 0}
          title={
            selectedFixtureIds.length === 0
              ? 'Pick at least one fixture in Step 1 first'
              : !statType.trim()
                ? 'Pick a stat in Step 2 first'
                : undefined
          }
        >
          {loadTriggered ? 'Reload candidates' : 'Load candidates'}
        </Button>
        {data && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowAll(s => !s)}
            disabled={!isEditable}
          >
            {showAll ? 'Show top 30' : `Show all (${total})`}
          </Button>
        )}
        {candidates.length > 0 && (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={handleSelectAllVisible}
              disabled={!isEditable}
            >
              Select all visible
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleClearSelections}
              disabled={!isEditable || (values.options || []).length === 0}
            >
              Clear
            </Button>
          </>
        )}
      </div>

      {isLoading && <p className="text-sm">Loading candidates…</p>}
      {!isLoading && isError && (
        <p className="text-sm text-red-600">
          Failed to load candidates. Check the backend logs and try again.
        </p>
      )}
      {!isLoading && !isError && loadTriggered && candidates.length === 0 && (
        <p className="text-sm text-amber-600">
          No candidates returned for the selected fixtures. Check the stat type or pick a different
          fixture set.
        </p>
      )}

      {candidates.length > 0 && (
        <div className="border rounded max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background border-b">
              <tr className="text-left">
                <th className="px-2 py-1 w-8"></th>
                <th className="px-2 py-1">{kind === 'team' ? 'Team' : 'Player'}</th>
                <th className="px-2 py-1">Position</th>
                <th className="px-2 py-1">Fixture</th>
                <th className="px-2 py-1 text-right">
                  {/* V1: label comes from feed-service (sport's primary
                      stat — e.g. "Batting Avg" for MLB) and may not match
                      the admin's chosen grading stat. Surface it
                      explicitly so the admin knows what they're seeing. */}
                  {data?.statValueLabel || statLabel || statType}
                </th>
              </tr>
            </thead>
            <tbody>
              {candidates.map(c => {
                const isChecked = selectedIds.has(String(c.externalId));
                return (
                  <tr key={c.externalId} className={`border-b ${isChecked ? 'bg-muted/40' : ''}`}>
                    <td className="px-2 py-1">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleCandidate(c)}
                        disabled={!isEditable}
                        aria-label={`Include ${c.name}`}
                      />
                    </td>
                    <td className="px-2 py-1 font-medium">{c.name}</td>
                    <td className="px-2 py-1 text-muted-foreground">{c.position || ''}</td>
                    <td className="px-2 py-1 text-muted-foreground">{c.fixtureLabel}</td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {c.statValue.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {truncated && (
            <div className="px-2 py-1 text-xs text-muted-foreground text-center border-t">
              Showing {candidates.length} of {total}. Use "Show all" to reveal the rest.
            </div>
          )}
        </div>
      )}

      {/* Counter only reflects leader candidates — i.e. options with a
          team/player external id. The form initialises new questions with
          2 empty option rows by default (team/player style); we ignore
          those here so the count doesn't lie before the admin loads
          candidates. */}
      {selectedIds.size > 0 && (
        <p className="text-sm">
          <span className="font-medium">{selectedIds.size}</span> candidate
          {selectedIds.size === 1 ? '' : 's'} selected.
        </p>
      )}
    </div>
  );
}

/**
 * LeaderStatTypePicker — sport-filtered stat dropdown for leader questions.
 *
 * Mirrors StatTypeSelectionSection's behaviour but doesn't require a fixture
 * to be selected (leader questions span the whole pickem). Reuses the same
 * /feed/stat-types endpoint so the admin sees the canonical list.
 */
function LeaderStatTypePicker({
  leagueId,
  kind,
  selectedStatType,
  selectedStatLabel,
  disabled,
  onSelect,
}: {
  leagueId?: string;
  kind: 'player' | 'team';
  selectedStatType: string;
  selectedStatLabel: string;
  disabled: boolean;
  onSelect: (statTypeCode: string, statTypeLabel: string) => void;
}) {
  // Restrict to stats the leader-question season-stat extractor supports —
  // see apps/feed-service/src/config/season-stat-extractors.ts. Stops the
  // admin picking a stat we can't rank candidates by (would show zero for
  // every player and the column header would be wrong).
  const {
    data: statTypes,
    isLoading,
    error,
  } = useGetFeedStatTypesQuery(leagueId, {
    forLeader: true,
    // Drop rate stats (batting avg) for team_leader since summing across a
    // roster is meaningless — server filters them out when kind=team.
    kind,
  });

  // Filter the stat list to match the leader kind so player_leader shows
  // player stats and team_leader shows team stats. Sports like soccer carry
  // genuinely distinct codes per category (soccer_goals vs soccer_team_goals),
  // so the split matters. Sports like MLB tag every code 'player' even though
  // the grader aggregates per-fixture player stats into team totals
  // (CrossFixtureLeaderGrader) — there the category filter would leave
  // team_leader empty, so we fall back to the full list. Finally dedupe by
  // code: NFL/NBA's canonical config carries both a player and a team entry
  // per code, and we only want one row per code in the dropdown.
  const filtered = useMemo(() => {
    const all = statTypes ?? [];
    const byKind = all.filter(s => s.category === kind);
    const list = byKind.length > 0 ? byKind : all;
    const seen = new Set<string>();
    return list.filter(s => {
      if (seen.has(s.code)) return false;
      seen.add(s.code);
      return true;
    });
  }, [statTypes, kind]);

  if (!leagueId) {
    return (
      <div className="text-sm text-muted-foreground">
        Pick a pickem with a sport first so we can list valid stats.
      </div>
    );
  }

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading stat types…</div>;
  }

  if (error) {
    return <div className="text-sm text-red-600">Failed to load stat types — try reloading.</div>;
  }

  if (filtered.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">No {kind} stats available for this sport.</div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="leader-stat-type">Stat to grade on *</Label>
      <Select
        value={selectedStatType}
        onValueChange={value => {
          // The admin picks by code; we also remember the display name so the
          // customer-facing modal title doesn't have to know stat codes.
          const match = filtered.find(s => s.code === value);
          const displayName = match?.name || value;
          onSelect(value, selectedStatLabel || displayName);
        }}
        disabled={disabled}
      >
        <SelectTrigger id="leader-stat-type">
          <SelectValue placeholder="Pick a stat" />
        </SelectTrigger>
        <SelectContent>
          {filtered.map(s => (
            <SelectItem key={s.code} value={s.code}>
              {s.name}
              <span className="text-muted-foreground ml-2 text-xs">({s.code})</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-xs text-muted-foreground">
        Used both for grading the winner and ranking candidates in the modal.
      </span>
    </div>
  );
}
